import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Container,
  Alert,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import IndexCard from "../../../shared/IndexCard";
import PaginatedListLayout from "../../../../shared/pagination/PaginatedListLayout";

const ProjectListPage = () => {
  const [tabIndex, setTabIndex] = useState(0); // 0: 募集中, 1: 募集終了
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const flashMessage = location.state?.flashMessage;

  const fetchProjects = useCallback(async (page = 1, nextTabIndex = 0) => {
    setLoading(true);
    const status = nextTabIndex === 0 ? "open" : "closed";

    try {
      const res = await fetch(
        `/company/projects.json?status=${status}&page=${page}`,
      );
      const data = await res.json();

      setProjects(data.projects ?? []);
      setPagination(data.pagination ?? null);

      // サーバー側でページ番号が丸められた場合に備えて、レスポンスに合わせて currentPage を同期
      if (data.pagination && typeof data.pagination.current_page === "number") {
        const serverPage = data.pagination.current_page;

        // 今回要求したページとサーバー返却ページが違うときだけ同期
        if (serverPage !== page) {
          setCurrentPage(serverPage);
        }
      }
    } catch (error) {
      console.error("プロジェクト取得に失敗しました", error);
      setProjects([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects(currentPage, tabIndex);
  }, [tabIndex, location.key, fetchProjects]);

  // ページ遷移やタブ切り替えのたびにスクロール位置を最上部にリセット
  const scrollToListTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  // タブ切り替え時の処理
  const handleTabChange = (_, newTabIndex) => {
    setTabIndex(newTabIndex);
    setCurrentPage(1);

    scrollToListTop();
  };

  // ページ切り替え時の処理
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProjects(page, tabIndex);
    scrollToListTop();
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 2,
        mb: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* フラッシュメッセージ (固定) */}
      {flashMessage && (
        <Box sx={{ flexShrink: 0 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      {/* 固定ヘッダー領域 (Tabsまで) */}
      <Box
        sx={{
          position: "sticky",
          top: "63px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0,
          mx: -2,
          px: 2,
          pt: 1.5,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            プロジェクト
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/company/projects/new")}
            sx={{ fontWeight: "bold" }}
          >
            ＋ 新規投稿
          </Button>
        </Box>

        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="募集中" />
          <Tab label="募集終了" />
        </Tabs>
      </Box>

      {/* 一覧エリア */}
      <Box
        sx={{
          flexGrow: 1,
          pr: 1,
          pb: 10,
        }}
      >
        <PaginatedListLayout
          items={projects}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyText="現在、投稿されたプロジェクトはありません。"
        >
          {projects.map((project) => (
            <IndexCard
              key={project.id}
              item={project}
              type="project"
              component={Link}
              to={`/company/projects/${project.id}`}
            />
          ))}
        </PaginatedListLayout>
      </Box>
    </Container>
  );
};

export default ProjectListPage;
